/**
 * Products Page
 *
 * Browse LUKHAS Products Suite - 356-file catalog across 9 categories
 */

import React, { useState } from 'react'
import { Package, Search, Grid, List, ExternalLink, Layers, Sparkles } from 'lucide-react'

interface Product {
  name: string
  category: string
  description: string
  features: string[]
  status: 'production' | 'beta' | 'development'
  path: string
  technologies: string[]
}

// Products Suite Catalog (356 files across 9 categories)
const PRODUCTS: Product[] = [
  // Communication Products
  {
    name: 'NIΛS - Non-Intrusive Lambda Symbolic',
    category: 'Communication',
    description: 'Dream Commerce engine with consent-based symbolic message delivery and emotional filtering',
    features: [
      'Dream-aware message scheduling',
      '7-tier consent management',
      'Emotional state filtering',
      'Lambda signatures (Λ-XXXXXXXX)',
      '81.2% test success rate',
    ],
    status: 'production',
    path: 'products/communication/nias/',
    technologies: ['Python', 'FastAPI', 'OpenAI GPT-4', 'DALL-E 3'],
  },
  {
    name: 'Voice Synthesis System',
    category: 'Communication',
    description: 'Multi-language voice synthesis with emotional tone adaptation',
    features: [
      'ElevenLabs integration',
      'Emotional tone detection',
      'Multi-language support',
      'Voice tag management',
    ],
    status: 'beta',
    path: 'products/communication/voice/',
    technologies: ['Python', 'ElevenLabs API', 'Audio Processing'],
  },

  // Intelligence Products
  {
    name: 'DΛST - Dynamic Lambda Symbol Tracker',
    category: 'Intelligence',
    description: 'Real-time symbolic context tracking with multi-source data integration',
    features: [
      '10 symbol categories',
      '9 data source types',
      'Confidence scoring',
      'Temporal pattern recognition',
      'Context coherence analysis',
    ],
    status: 'production',
    path: 'products/intelligence/dast/',
    technologies: ['Python', 'FastAPI', 'ML', 'Real-time Processing'],
  },
  {
    name: 'Glyph Recognition System',
    category: 'Intelligence',
    description: 'Λ symbolic glyph recognition and interpretation engine',
    features: [
      'Glyph pattern detection',
      'Lambda consciousness markers',
      'Symbolic interpretation',
      'Multi-modal analysis',
    ],
    status: 'beta',
    path: 'products/intelligence/glyph/',
    technologies: ['Python', 'Computer Vision', 'Pattern Recognition'],
  },
  {
    name: 'Contextual Assistant',
    category: 'Intelligence',
    description: 'AI-powered contextual assistance with consciousness awareness',
    features: [
      'Context-aware suggestions',
      'Proactive assistance',
      'Learning from interactions',
      'Task automation',
    ],
    status: 'development',
    path: 'products/intelligence/assistant/',
    technologies: ['Python', 'NLP', 'ML', 'Consciousness Integration'],
  },

  // Experience Products
  {
    name: 'Interactive Storytelling Engine',
    category: 'Experience',
    description: 'Adaptive narrative generation with user interaction',
    features: [
      'Dynamic story branching',
      'Character evolution',
      'Emotional arc tracking',
      'Multi-ending narratives',
    ],
    status: 'beta',
    path: 'products/experience/storytelling/',
    technologies: ['Python', 'GPT-4', 'State Management'],
  },
  {
    name: 'Emotion Tracking System',
    category: 'Experience',
    description: 'Real-time emotional state monitoring and adaptation',
    features: [
      'Biometric integration',
      'Sentiment analysis',
      'Emotion prediction',
      'Adaptive response system',
    ],
    status: 'beta',
    path: 'products/experience/emotion/',
    technologies: ['Python', 'ML', 'Biometric APIs'],
  },
  {
    name: 'Gamification Framework',
    category: 'Experience',
    description: 'Achievement system with progression mechanics',
    features: [
      'Badge system',
      'Level progression',
      'Quest management',
      'Reward distribution',
    ],
    status: 'production',
    path: 'products/experience/gamification/',
    technologies: ['Python', 'PostgreSQL', 'Redis'],
  },

  // Enterprise Products
  {
    name: 'Multi-Tenant Manager',
    category: 'Enterprise',
    description: 'Enterprise-grade multi-tenant isolation and management',
    features: [
      'Tenant isolation',
      'Resource quotas',
      'Billing integration',
      'Admin dashboard',
    ],
    status: 'production',
    path: 'products/enterprise/tenancy/',
    technologies: ['Python', 'PostgreSQL', 'Redis'],
  },
  {
    name: 'Analytics Pipeline',
    category: 'Enterprise',
    description: 'Real-time analytics and business intelligence',
    features: [
      'Event tracking',
      'Funnel analysis',
      'Custom dashboards',
      'Export capabilities',
    ],
    status: 'production',
    path: 'products/enterprise/analytics/',
    technologies: ['Python', 'ClickHouse', 'Grafana'],
  },
  {
    name: 'Compliance Framework',
    category: 'Enterprise',
    description: 'GDPR, CCPA, and SOC2 compliance automation',
    features: [
      'Data mapping',
      'Consent management',
      'Audit logging',
      'Report generation',
    ],
    status: 'production',
    path: 'products/enterprise/compliance/',
    technologies: ['Python', 'Audit Logs', 'Compliance APIs'],
  },

  // Security Products
  {
    name: 'QRG - Quantum-Resistant Guardian',
    category: 'Security',
    description: 'Post-quantum cryptography and security enforcement',
    features: [
      'Quantum-resistant encryption',
      'Key rotation',
      'Threat detection',
      'Security auditing',
    ],
    status: 'beta',
    path: 'products/security/qrg/',
    technologies: ['Python', 'Cryptography', 'Quantum Algorithms'],
  },
  {
    name: 'Identity Management (ΛiD)',
    category: 'Security',
    description: 'Lambda-branded identity and authentication system',
    features: [
      'WebAuthn support',
      'Multi-factor authentication',
      'Session management',
      'OAuth2/OIDC provider',
    ],
    status: 'production',
    path: 'products/security/identity/',
    technologies: ['Python', 'FastAPI', 'Cryptography'],
  },

  // Content Products
  {
    name: 'Content Generation Engine',
    category: 'Content',
    description: 'AI-powered content creation and optimization',
    features: [
      'Multi-format generation',
      'SEO optimization',
      'Tone adaptation',
      'Version control',
    ],
    status: 'beta',
    path: 'products/content/generation/',
    technologies: ['Python', 'GPT-4', 'NLP'],
  },
  {
    name: 'Knowledge Base System',
    category: 'Content',
    description: 'Structured knowledge management and retrieval',
    features: [
      'Semantic search',
      'Version control',
      'Categorization',
      'Export formats',
    ],
    status: 'production',
    path: 'products/content/knowledge_base/',
    technologies: ['Python', 'Elasticsearch', 'Vector DB'],
  },

  // Automation Products
  {
    name: 'Workflow Orchestrator',
    category: 'Automation',
    description: 'Complex workflow automation and scheduling',
    features: [
      'Visual workflow builder',
      'Task scheduling',
      'Error handling',
      'Parallel execution',
    ],
    status: 'production',
    path: 'products/automation/workflows/',
    technologies: ['Python', 'Celery', 'Redis', 'RabbitMQ'],
  },
  {
    name: 'Integration Hub',
    category: 'Automation',
    description: 'Third-party service integration management',
    features: [
      'API connectors',
      'Webhook management',
      'Data transformation',
      'Error recovery',
    ],
    status: 'beta',
    path: 'products/automation/integrations/',
    technologies: ['Python', 'REST APIs', 'GraphQL'],
  },

  // Infrastructure Products
  {
    name: 'Monitoring Stack',
    category: 'Infrastructure',
    description: 'Comprehensive system monitoring and alerting',
    features: [
      'Metrics collection',
      'Log aggregation',
      'Alerting rules',
      'Dashboards',
    ],
    status: 'production',
    path: 'products/infrastructure/monitoring/',
    technologies: ['Prometheus', 'Grafana', 'Loki'],
  },
  {
    name: 'Deployment Pipeline',
    category: 'Infrastructure',
    description: 'CI/CD automation and deployment management',
    features: [
      'Automated testing',
      'Blue-green deployments',
      'Rollback capability',
      'Environment management',
    ],
    status: 'production',
    path: 'products/infrastructure/deployment/',
    technologies: ['GitHub Actions', 'Docker', 'Kubernetes'],
  },

  // Frontend Products
  {
    name: 'Component Library',
    category: 'Frontend',
    description: 'Reusable React components with Λ branding',
    features: [
      'Tailwind styling',
      'Dark mode support',
      'Accessibility compliant',
      'Storybook docs',
    ],
    status: 'production',
    path: 'products/frontend/components/',
    technologies: ['React', 'TypeScript', 'Tailwind', 'Storybook'],
  },
]

export const ProductsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Get unique categories and statuses
  const categories = ['all', ...Array.from(new Set(PRODUCTS.map(p => p.category)))]
  const statuses = ['all', 'production', 'beta', 'development']

  // Filter products
  const filteredProducts = PRODUCTS.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.technologies.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()))

    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory
    const matchesStatus = selectedStatus === 'all' || product.status === selectedStatus

    return matchesSearch && matchesCategory && matchesStatus
  })

  // Get status badge style
  const getStatusBadge = (status: string) => {
    const styles = {
      production: 'bg-green-500/20 text-green-400 border-green-500/30',
      beta: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    }
    return styles[status as keyof typeof styles]
  }

  // Get category icon
  const getCategoryIcon = () => {
    return <Layers className="w-4 h-4" />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg">
                <Package className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  Products Suite
                </h1>
                <p className="text-gray-400 mt-1">
                  Λ 356-File Catalog Across 9 Categories
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{filteredProducts.length}</p>
                <p className="text-xs text-gray-400">Products</p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="mt-6 flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products, technologies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-3">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>

              {/* View Toggle */}
              <div className="flex items-center space-x-1 bg-gray-800/50 border border-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Display */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No products found</p>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-4'}>
            {filteredProducts.map((product, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg p-6 hover:border-purple-500/50 transition-all group"
              >
                {/* Product Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getCategoryIcon()}
                      <h3 className="text-lg font-semibold text-white group-hover:text-purple-400 transition-colors">
                        {product.name}
                      </h3>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ml-3 ${getStatusBadge(product.status)}`}>
                    {product.status}
                  </span>
                </div>

                {/* Features */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Key Features</p>
                  <ul className="space-y-1">
                    {product.features.slice(0, 3).map((feature, fIndex) => (
                      <li key={fIndex} className="text-sm text-gray-300 flex items-center space-x-2">
                        <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technologies */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {product.technologies.map((tech, tIndex) => (
                      <span
                        key={tIndex}
                        className="px-2 py-1 bg-gray-800/50 text-gray-400 text-xs rounded"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                  <span className="text-xs text-gray-500">{product.category}</span>
                  <a
                    href={`https://github.com/LukhasAI/Lukhas/tree/main/${product.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-xs text-gray-400 hover:text-purple-400 transition-colors"
                  >
                    <span>View Source</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Category Stats */}
        <div className="mt-8 grid grid-cols-3 md:grid-cols-5 gap-4">
          {categories.filter(c => c !== 'all').slice(0, 5).map(category => {
            const count = PRODUCTS.filter(p => p.category === category).length
            return (
              <div
                key={category}
                className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg text-center"
              >
                <p className="text-2xl font-bold text-white">{count}</p>
                <p className="text-xs text-gray-400 mt-1">{category}</p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ProductsPage
